import usePageTitle from '../../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { admin } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Plus, Trash2, Edit, Image, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { getImageUrl } from '../../lib/api';

const AdminBanners = () => {
  usePageTitle('Manage Banners');
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Form state
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [link, setLink] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [order, setOrder] = useState(0);

  const loadBanners = async () => {
    try {
      const response = await admin.getBanners();
      setBanners(response.data.banners || []);
    } catch (error) {
      console.error('Failed to load banners:', error);
      toast.error('Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBanners();
  }, []);

  const resetForm = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setTitle('');
    setSubtitle('');
    setLink('');
    setIsActive(true);
    setOrder(banners.length);
    setEditingBanner(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleOpenDialog = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setTitle(banner.title || '');
      setSubtitle(banner.subtitle || '');
      setLink(banner.link || '');
      setIsActive(banner.is_active);
      setOrder(banner.order);
      setPreviewUrl(getImageUrl(banner.image_url));
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingBanner && !selectedFile) {
      toast.error('Please select an image');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      if (selectedFile) {
        formData.append('image', selectedFile);
      }
      formData.append('title', title || '');
      formData.append('subtitle', subtitle || '');
      formData.append('link', link || '');
      formData.append('is_active', isActive);
      formData.append('order', order);

      if (editingBanner) {
        await admin.updateBanner(editingBanner.id, formData);
        toast.success('Banner updated');
      } else {
        await admin.createBanner(formData);
        toast.success('Banner uploaded');
      }

      setDialogOpen(false);
      resetForm();
      loadBanners();
    } catch (error) {
      console.error('Failed to save banner:', error);
      toast.error(error.message || 'Failed to save banner');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    
    try {
      await admin.deleteBanner(bannerId);
      toast.success('Banner deleted');
      loadBanners();
    } catch (error) {
      toast.error('Failed to delete banner');
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      const formData = new FormData();
      formData.append('title', banner.title || '');
      formData.append('subtitle', banner.subtitle || '');
      formData.append('link', banner.link || '');
      formData.append('is_active', !banner.is_active);
      formData.append('order', banner.order);
      
      await admin.updateBanner(banner.id, formData);
      toast.success(banner.is_active ? 'Banner deactivated' : 'Banner activated');
      loadBanners();
    } catch (error) {
      toast.error('Failed to update banner');
    }
  };

  const handleReorder = async (bannerId, direction) => {
    const index = banners.findIndex(b => b.id === bannerId);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= banners.length) return;
    
    // Swap orders
    const currentBanner = banners[index];
    const swapBanner = banners[newIndex];
    
    try {
      // Update current banner
      const formData1 = new FormData();
      formData1.append('title', currentBanner.title || '');
      formData1.append('subtitle', currentBanner.subtitle || '');
      formData1.append('link', currentBanner.link || '');
      formData1.append('is_active', currentBanner.is_active);
      formData1.append('order', swapBanner.order);
      await admin.updateBanner(currentBanner.id, formData1);
      
      // Update swap banner
      const formData2 = new FormData();
      formData2.append('title', swapBanner.title || '');
      formData2.append('subtitle', swapBanner.subtitle || '');
      formData2.append('link', swapBanner.link || '');
      formData2.append('is_active', swapBanner.is_active);
      formData2.append('order', currentBanner.order);
      await admin.updateBanner(swapBanner.id, formData2);
      
      loadBanners();
    } catch (error) {
      toast.error('Failed to reorder banners');
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-48 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="admin-banners-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Homepage Banners</h1>
          <p className="text-[#64748B] text-sm mt-1">
            Manage hero banners that appear on the homepage. Recommended size: 1920x600px
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => handleOpenDialog()} 
              className="bg-[#002FA7] hover:bg-[#0025a0]"
              disabled={banners.length >= 5}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Banner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingBanner ? 'Edit Banner' : 'Add New Banner'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label>Banner Image {!editingBanner && '*'}</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  {previewUrl ? (
                    <div className="relative">
                      <img 
                        src={previewUrl} 
                        alt="Preview" 
                        className="w-full h-32 object-cover rounded"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => document.getElementById('banner-file').click()}
                      >
                        Change Image
                      </Button>
                    </div>
                  ) : (
                    <div 
                      className="py-8 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => document.getElementById('banner-file').click()}
                    >
                      <Image className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Click to upload banner image</p>
                      <p className="text-xs text-gray-400 mt-1">Recommended: 1920x600px, JPG/PNG</p>
                    </div>
                  )}
                  <input
                    id="banner-file"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              </div>

              {/* Title (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="title">Title (Optional)</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Banner headline text"
                />
              </div>

              {/* Subtitle (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="subtitle">Subtitle (Optional)</Label>
                <Input
                  id="subtitle"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  placeholder="Supporting text"
                />
              </div>

              {/* Link (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="link">Link (Optional)</Label>
                <Input
                  id="link"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder="/products or /categories/t-shirts"
                />
              </div>

              {/* Active Switch */}
              <div className="flex items-center justify-between">
                <Label htmlFor="active">Active</Label>
                <Switch
                  id="active"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#002FA7] hover:bg-[#0025a0]"
                  disabled={uploading}
                >
                  {uploading ? 'Saving...' : editingBanner ? 'Update' : 'Upload'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info */}
      {banners.length >= 5 && (
        <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-sm text-yellow-800">
          Maximum of 5 banners reached. Delete a banner to add a new one.
        </div>
      )}

      {/* Banners List */}
      {banners.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No banners yet</h3>
            <p className="text-gray-500 mb-4">Add your first homepage banner to get started</p>
            <Button onClick={() => handleOpenDialog()} className="bg-[#002FA7] hover:bg-[#0025a0]">
              <Plus className="w-4 h-4 mr-2" />
              Add Banner
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {banners.map((banner, index) => (
            <Card key={banner.id} className={`${!banner.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Drag Handle / Order */}
                  <div className="flex flex-col items-center gap-1">
                    <button
                      onClick={() => handleReorder(banner.id, 'up')}
                      disabled={index === 0}
                      className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <button
                      onClick={() => handleReorder(banner.id, 'down')}
                      disabled={index === banners.length - 1}
                      className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Image Preview */}
                  <div className="w-48 h-24 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                    <img 
                      src={getImageUrl(banner.image_url)} 
                      alt={banner.title || 'Banner'} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900 truncate">
                        {banner.title || 'No title'}
                      </h3>
                      {!banner.is_active && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                    {banner.subtitle && (
                      <p className="text-sm text-gray-500 truncate">{banner.subtitle}</p>
                    )}
                    {banner.link && (
                      <p className="text-xs text-blue-600 truncate mt-1">Link: {banner.link}</p>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={banner.is_active}
                      onCheckedChange={() => handleToggleActive(banner)}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenDialog(banner)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(banner.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Info */}
      {banners.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-1">Preview</h4>
          <p className="text-sm text-blue-800">
            Active banners will auto-slide every 5 seconds on the homepage.
            Visit the <a href="/" className="underline">homepage</a> to see them in action.
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminBanners;
